import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History, RefreshCw, AlertTriangle, CheckCircle2, FileText, User as UserIcon, Link2, Ban } from "lucide-react";
import { toast } from "sonner";

function fmtDataHora(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { timeZone: "America/Fortaleza" });
  } catch {
    return iso;
  }
}

export default function DossieHistoryCard({ inscricaoId }) {
  const [items, setItems] = useState([]);
  const [activeShares, setActiveShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    if (!inscricaoId) return;
    setLoading(true);
    try {
      const [hist, shares] = await Promise.all([
        api.get(`/inscricoes/${inscricaoId}/dossie-history`),
        api.get(`/inscricoes/${inscricaoId}/dossie/shares/ativos`),
      ]);
      setItems(hist.data.items || []);
      setActiveShares(shares.data.items || []);
    } catch (e) {
      logger.error("Falha ao carregar histórico de dossiês", e);
    } finally { setLoading(false); }
  }, [inscricaoId]);

  useEffect(() => { load(); }, [load]);

  const revokeShare = async (jti) => {
    if (!window.confirm("Revogar este link? O destinatário perderá acesso imediatamente.")) return;
    setRevoking(jti);
    try {
      await api.post(`/inscricoes/${inscricaoId}/dossie/share/${jti}/revoke`);
      toast.success("Link revogado — qualquer novo acesso será bloqueado");
      load();
    } catch (e) {
      logger.error("Falha ao revogar link", e);
      toast.error(e.response?.data?.detail || "Falha ao revogar");
    } finally { setRevoking(null); }
  };

  return (
    <Card data-testid="dossie-history-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="text-base text-[#083CA6] flex items-center gap-2">
            <History className="w-4 h-4" /> Histórico de Dossiês
          </CardTitle>
          <CardDescription>
            {items.length === 0
              ? "Nenhum dossiê foi gerado para esta inscrição ainda."
              : `${items.length} geração(ões) registrada(s).`}
          </CardDescription>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="btn-dossie-history-refresh"
          aria-label="Atualizar"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {activeShares.length > 0 && (
          <div className="mb-4 border rounded border-sky-200 bg-sky-50/40 p-2">
            <div className="text-xs font-semibold text-sky-800 flex items-center gap-1 mb-2">
              <Link2 className="w-3.5 h-3.5" />
              Links ativos ({activeShares.length})
            </div>
            <ul className="space-y-1.5" data-testid="active-shares-list">
              {activeShares.map((s) => {
                const exp = s.exp ? new Date(s.exp).toLocaleString("pt-BR") : "—";
                return (
                  <li
                    key={s.jti}
                    className="flex items-center justify-between gap-2 text-xs bg-white border border-sky-100 rounded px-2 py-1.5"
                    data-testid={`share-${s.jti}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[10px] text-slate-500 truncate">
                        jti: {s.jti.slice(0, 8)}…
                      </div>
                      <div className="text-slate-700">
                        Expira: <strong>{exp}</strong>
                        {s.issued_by_nome && <span className="text-slate-500"> · por {s.issued_by_nome}</span>}
                      </div>
                    </div>
                    <Button
                      size="sm" variant="outline"
                      className="h-7 border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => revokeShare(s.jti)}
                      disabled={revoking === s.jti}
                      data-testid={`btn-revoke-${s.jti}`}
                      title="Revogar este link"
                    >
                      <Ban className="w-3 h-3 mr-1" />
                      {revoking === s.jti ? "Revogando..." : "Revogar"}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {items.length === 0 && !loading && (
          <div className="text-xs text-slate-500 italic flex items-center gap-2 p-2">
            <FileText className="w-4 h-4" />
            Gere o Dossiê pela aba "Dossiê" ou pelo botão na lista para registrar aqui.
          </div>
        )}
        {items.length > 0 && (
          <ul className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {items.map((it, idx) => {
              const meta = it.meta || {};
              const incompleto = meta.incompleto === true;
              const origem = meta.origem === "bulk" ? "Lote (ZIP)" : "Individual";
              const key = it.id || `${it.timestamp || it.created_at || "h"}-${idx}`;
              return (
                <li
                  key={key}
                  className={`border rounded p-2 text-xs ${
                    incompleto ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-slate-50/40"
                  }`}
                  data-testid={`dossie-history-item-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="font-semibold text-slate-700 flex items-center gap-1">
                      <UserIcon className="w-3 h-3" />
                      {it.actor_nome || "—"}
                      <span className="text-slate-500 font-normal">
                        ({it.actor_role === "super_admin" ? "Super Admin" : (it.actor_role || "—")})
                      </span>
                    </div>
                    <Badge
                      className={incompleto
                        ? "bg-amber-100 text-amber-800 border-amber-300"
                        : "bg-emerald-100 text-emerald-800 border-emerald-300"}
                    >
                      {incompleto
                        ? <><AlertTriangle className="w-3 h-3 mr-1" /> Incompleto</>
                        : <><CheckCircle2 className="w-3 h-3 mr-1" /> Completo</>}
                    </Badge>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 flex-wrap text-slate-600">
                    <span>{fmtDataHora(it.timestamp)}</span>
                    <span className="font-mono text-[10px]">IP: {it.ip || "—"}</span>
                  </div>
                  <div className="mt-1 text-slate-600">
                    <span className="inline-block bg-white border rounded px-1.5 py-0.5 mr-2">
                      Origem: {origem}
                    </span>
                    {typeof meta.size_bytes === "number" && (
                      <span className="inline-block bg-white border rounded px-1.5 py-0.5">
                        {(meta.size_bytes / 1024).toFixed(1)} KB
                      </span>
                    )}
                  </div>
                  {incompleto && Array.isArray(meta.faltando) && meta.faltando.length > 0 && (
                    <div className="mt-1 text-amber-800">
                      <strong>Faltando:</strong> {meta.faltando.join(", ")}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
